// 화면 안 확인창·알림창(2026-10-28 사용자: "기본 동작 걷어내기") — 브라우저 confirm/alert는 폰에서 화면이 끊기고 모양도 사이트와 달라서.
// 쓰는 법: if (!(await confirmDialog('지울까요?'))) return;  /  notify('저장 실패: …')
// Enter = 확인, Esc·바깥 누르기 = 취소. 줄바꿈(\n)은 그대로 보인다. App에 <DialogHost />를 한 번 둔다.
import React, { useEffect, useRef, useSyncExternalStore } from 'react';

type Req = { message: string; kind: 'confirm' | 'alert'; resolve: (ok: boolean) => void } | null;
let current: Req = null;
const subs = new Set<() => void>();
const set = (r: Req) => {
  current = r;
  subs.forEach((f) => f());
};

const open = (message: string, kind: 'confirm' | 'alert') =>
  new Promise<boolean>((resolve) => {
    current?.resolve(false); // 이미 떠 있던 창은 취소로 닫는다
    set({ message, kind, resolve });
  });

export const confirmDialog = (message: string) => open(message, 'confirm');
export const notify = (message: string) => open(message, 'alert').then(() => undefined);

export const DialogHost: React.FC = () => {
  const req = useSyncExternalStore(
    (f) => {
      subs.add(f);
      return () => subs.delete(f);
    },
    () => current,
  );
  const okRef = useRef<HTMLButtonElement>(null);
  const close = (ok: boolean) => {
    const r = current;
    set(null);
    r?.resolve(ok);
  };
  useEffect(() => {
    if (!req) return;
    okRef.current?.focus();
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); close(false); }
      else if (e.key === 'Enter') { e.preventDefault(); close(true); }
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- close는 모듈 상태만 쓴다
  }, [req]);
  if (!req) return null;
  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/60 px-4" onClick={() => close(false)} role="presentation">
      <div role={req.kind === 'confirm' ? 'alertdialog' : 'dialog'} aria-modal="true" onClick={(e) => e.stopPropagation()} className="w-full max-w-sm rounded-lg border border-white/15 bg-[#16161f] p-4 text-sm text-text-primary shadow-xl">
        <p className="max-h-[60vh] overflow-y-auto whitespace-pre-line break-words">{req.message}</p>
        <div className="mt-4 flex justify-end gap-2">
          {req.kind === 'confirm' && (
            <button type="button" onClick={() => close(false)} className="rounded border border-white/15 px-3 py-1.5 text-text-secondary hover:text-text-primary">
              취소
            </button>
          )}
          <button ref={okRef} type="button" onClick={() => close(true)} className="rounded border border-primary-blue/60 bg-primary-blue/20 px-3 py-1.5 text-text-primary hover:bg-primary-blue/30">
            확인
          </button>
        </div>
      </div>
    </div>
  );
};
